/*
 * Front-end only enhancement for the Stay.ai pack-tier variant picker
 * (sections/stayai-lvbb-clone-v2.liquid). Restyles the native
 * variant-picker output into the same "Choose your plan" list used by
 * the live theme's snippets/livera-buybox-standard.liquid (tags, radio,
 * price + strike, "What's included" expand on the default plan), and
 * defaults the selection to match live.
 *
 * Does not touch Stay.ai's own config/state (settings_data.json,
 * stayai-pdp.js/liquid) or move any of its DOM nodes — mostly decorates
 * the native variant-picker labels and adds a static header row. The one
 * exception is setupSubscriptionFrequencySync() below, which repopulates
 * the frequency <select> per pack (see its own comment for why) using
 * only the plans already configured in the Stay.ai portal.
 *
 * The variant-picker re-fetches/morphs its own markup from the server
 * on every selection change, which wipes any DOM we appended. A
 * MutationObserver re-applies the enhancement after every such morph
 * (each call is idempotent per label via data-lvbb-enhanced).
 */
(function () {
  // `unit` = price per pouch, shown next to `meta` so the ladder is readable
  // without the customer doing the division. Keep it in sync with price/meta.
  var PACKS = {
    '1 Pack': {
      meta: '1 pouch',
      unit: '$39',
      price: '$39',
      compare: '$98'
    },
    'BUY 2 GET 1 FREE': {
      meta: '3 pouches',
      unit: '$26',
      price: '$78',
      compare: '$196',
      tagBogo: 'Buy 2, Get 1 Free',
      expand: true,
      expandValue: '$47 value',
      gifts: [
        { name: '3rd pouch, every delivery', was: '$39' },
        { name: 'Shipping, always', was: '$8' },
        { name: 'Pause or cancel anytime' }
      ]
    },
    'Buy 3 Get 2 Free': {
      meta: '5 pouches',
      unit: '$23.40',
      price: '$117',
      compare: '$294',
      tagPopular: 'Most popular',
      tagBogo: 'Buy 3, Get 2 Free',
      expand: true,
      expandValue: '$86 value',
      gifts: [
        { name: '2 pouches FREE, every delivery', was: '$78' },
        { name: 'Shipping, always', was: '$8' },
        { name: 'Pause or cancel anytime' }
      ]
    }
  };
  var DEFAULT_PACK = 'BUY 2 GET 1 FREE';

  function bonusStackHTML(assetUrl) {
    return (
      '<div class="lvbb-bonus-stack">' +
      '<img src="' + assetUrl + '" class="lvbb-bonus-3" alt="" aria-hidden="true" loading="lazy">' +
      '<img src="' + assetUrl + '" class="lvbb-bonus-2" alt="" aria-hidden="true" loading="lazy">' +
      '<img src="' + assetUrl + '" class="lvbb-bonus-1" alt="Livera pouches included with this plan" loading="lazy">' +
      '</div>'
    );
  }

  function ensureHeader(section, fieldset) {
    if (!fieldset) return;
    var existing = section.querySelector(':scope > * .lvbb-plan-header, .lvbb-plan-header');
    if (existing && existing.isConnected && fieldset.previousElementSibling === existing) {
      ensureStockBar(section, fieldset);
      return;
    }
    if (existing) existing.remove();
    var header = document.createElement('div');
    header.className = 'lvbb-plan-header';
    header.innerHTML =
      '<div class="lvbb-plan-label">Choose your plan:</div>' +
      '<div class="lvbb-guarantee">' +
      '<svg width="14" height="16" viewBox="0 0 24 24" fill="none" stroke="rgb(255,107,9)" stroke-width="2">' +
      '<path d="M12 2 4 5v7c0 5 3.5 9 8 10 4.5-1 8-5 8-10V5z"/></svg> 30-day guarantee</div>';
    fieldset.parentNode.insertBefore(header, fieldset);
    ensureStockBar(section, fieldset);
  }

  // Restored from snippets/livera-buybox-standard.liquid: scarcity bar
  // sitting between the plan header and the plan cards.
  function ensureStockBar(section, fieldset) {
    var existing = section.querySelector('.lvbb-stock');
    if (existing && existing.isConnected && fieldset.previousElementSibling === existing) return;
    if (existing) existing.remove();
    var stock = document.createElement('div');
    stock.className = 'lvbb-stock';
    stock.setAttribute('aria-hidden', 'true');
    stock.innerHTML =
      '<div class="lvbb-stock-track"><span class="lvbb-stock-fill"></span></div>' +
      '<span class="lvbb-stock-label">89% claimed</span>';
    fieldset.parentNode.insertBefore(stock, fieldset);
  }

  function decorateLabel(label, input, cfg, bonusUrl) {
    label.dataset.lvbbEnhanced = 'true';

    var textEl = label.querySelector('.variant-option__button-label__text');
    var pillEl = label.querySelector('.variant-option__button-label__pill');
    if (pillEl) pillEl.remove();

    // Stay.ai renders the raw variant title here (e.g. "BUY 2 GET 1 FREE",
    // all-caps because that's the literal Shopify variant name). Live's
    // buybox shows a nicer hand-cased label instead ("Buy 2, Get 1 Free") —
    // swap in cfg.tagBogo for display only; the variant/selection itself
    // is untouched.
    if (textEl && cfg.tagBogo) textEl.textContent = cfg.tagBogo;

    // Only "Most popular" is rendered as a badge. The old tagBogo badge
    // repeated the card's own title verbatim ("Buy 2, Get 1 Free" above a card
    // already titled "Buy 2, Get 1 Free") and stacked two badges on the third
    // card. cfg.tagBogo is still used above to hand-case the title itself.
    if (cfg.tagPopular) {
      label.classList.add('lvbb-has-tags');
      var tagsRow = document.createElement('div');
      tagsRow.className = 'lvbb-plan-tags-row';
      var pop = document.createElement('span');
      pop.className = 'lvbb-plan-tag-popular';
      pop.textContent = cfg.tagPopular;
      tagsRow.appendChild(pop);
      label.insertBefore(tagsRow, label.firstChild);
    }

    var row = document.createElement('div');
    row.className = 'lvbb-plan-row';

    var radio = document.createElement('div');
    radio.className = 'lvbb-plan-radio';

    var info = document.createElement('div');
    info.className = 'lvbb-plan-info';
    if (textEl) info.appendChild(textEl);
    var meta = document.createElement('div');
    meta.className = 'lvbb-plan-meta';
    meta.textContent = cfg.unit ? cfg.meta + ' · ' + cfg.unit + ' each' : cfg.meta;
    info.appendChild(meta);

    var priceCol = document.createElement('div');
    priceCol.className = 'lvbb-plan-price-col';
    var price = document.createElement('span');
    price.className = 'lvbb-plan-price';
    price.textContent = cfg.price;
    var strike = document.createElement('span');
    strike.className = 'lvbb-plan-strike';
    strike.textContent = cfg.compare;
    price.appendChild(strike);
    priceCol.appendChild(price);

    row.appendChild(radio);
    row.appendChild(info);
    row.appendChild(priceCol);
    label.appendChild(row);

    if (cfg.expand) {
      var expand = document.createElement('div');
      expand.className = 'lvbb-plan-expand';
      var title = document.createElement('div');
      title.className = 'lvbb-plan-expand-title';
      title.textContent = "What's included";
      if (cfg.expandValue) {
        var valueEl = document.createElement('span');
        valueEl.className = 'lvbb-plan-expand-value';
        valueEl.textContent = ' — ' + cfg.expandValue;
        title.appendChild(valueEl);
      }
      var body = document.createElement('div');
      body.className = 'lvbb-plan-expand-body';
      var ul = document.createElement('ul');
      (cfg.gifts || []).forEach(function (gift) {
        var li = document.createElement('li');
        var name = document.createElement('span');
        name.textContent = gift.name;
        li.appendChild(name);
        if (gift.was) {
          var was = document.createElement('s');
          was.textContent = gift.was;
          li.appendChild(was);
        }
        var free = document.createElement('b');
        free.textContent = 'FREE';
        li.appendChild(free);
        ul.appendChild(li);
      });
      body.appendChild(ul);
      if (bonusUrl) body.insertAdjacentHTML('beforeend', bonusStackHTML(bonusUrl));
      expand.appendChild(title);
      expand.appendChild(body);
      label.appendChild(expand);
    }
  }

  function enhanceSection(section) {
    var fieldset = section.querySelector('variant-picker fieldset.variant-option--buttons');
    var labels = section.querySelectorAll('variant-picker label.variant-option__button-label');
    if (!labels.length) return;

    ensureHeader(section, fieldset);

    var bonusUrl = section.dataset.bonusPouchUrl || '';
    var hasCheckedAllowedPack = false;
    var defaultInput = null;

    labels.forEach(function (label) {
      var input = label.querySelector('input[type="radio"]');
      if (!input) return;

      var cfg = PACKS[input.value];
      if (!cfg) {
        // "Try Once" is a deliberate, valid selection (picked via
        // setupTryOnce() below) even though it's not a decorated pack
        // tile. Counting it here stops this same check from undoing that
        // selection: without it, every mutation-triggered re-run of this
        // function saw no *decorated* pack checked and force-reselected
        // DEFAULT_PACK, silently overriding "Try Once" back to whichever
        // pack was selected before.
        if (input.checked && input.value === 'Try Once') hasCheckedAllowedPack = true;
        return; // other legacy values — CSS already hides them
      }

      if (input.value === DEFAULT_PACK) defaultInput = input;
      if (input.checked) hasCheckedAllowedPack = true;

      if (label.dataset.lvbbEnhanced) return;
      decorateLabel(label, input, cfg, bonusUrl);
    });

    if (!hasCheckedAllowedPack && defaultInput && !defaultInput.checked) {
      defaultInput.click();
    }
  }

  function observeSection(section) {
    if (section.dataset.lvbbObserved) return;
    section.dataset.lvbbObserved = 'true';
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        enhanceSection(section);
      });
    });
    observer.observe(section, { childList: true, subtree: true });
  }

  // Clear the cart right before the main Add to Cart button adds the
  // selected pack, but ONLY on the same two landings that clear it on the
  // live theme's snippets/livera-buybox-standard.liquid (a single-item
  // funnel) — first-order-liver-shots and marcus-weekend-monday. Every
  // other page (home, PDP, other LPs) keeps the cart untouched, matching
  // live. Scope is read from data-clear-cart="true" on the closest
  // #buyboxForm (set by snippets/livera-buybox-standard-stayai.liquid from
  // its `landing` param). A short-lived "armed" flag guards the fetch
  // patch so quick-add/recommendation carousels further down the page are
  // never affected.
  function findMainAddToCart() {
    var selectors = ['.bb-main-cta', 'add-to-cart-component', '#buyboxForm button[type="submit"]'];
    for (var s = 0; s < selectors.length; s++) {
      var candidates = document.querySelectorAll(selectors[s]);
      for (var i = 0; i < candidates.length; i++) {
        if (!candidates[i].closest('dialog, quick-add-modal, [data-quick-add]')) {
          return candidates[i];
        }
      }
    }
    return null;
  }

  function shouldClearCartFor(button) {
    var form = button.closest('#buyboxForm') || document.getElementById('buyboxForm');
    return !!form && form.dataset.clearCart === 'true';
  }

  function setupClearCartOnAdd() {
    var button = findMainAddToCart();
    if (!button || button.dataset.lvbbClearWired) return;
    button.dataset.lvbbClearWired = 'true';

    var armed = false;
    var armTimer = null;
    button.addEventListener(
      'click',
      function () {
        if (!shouldClearCartFor(button)) return;
        armed = true;
        clearTimeout(armTimer);
        armTimer = setTimeout(function () {
          armed = false;
        }, 4000);
      },
      true
    );

    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (armed && /\/cart\/add(\.js)?(\?|$)/.test(url)) {
        armed = false;
        clearTimeout(armTimer);
        return originalFetch
          .call(window, '/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } })
          .catch(function () {})
          .then(function () {
            return originalFetch.call(window, input, init);
          });
      }
      return originalFetch.apply(window, arguments);
    };
  }

  // Restored from snippets/livera-buybox-standard.liquid: the "Try once"
  // link selects the product's own "Try Once" variant (hidden from the
  // pack grid by the legacy-value CSS rule) and reuses the normal
  // add-to-cart flow, so it inherits clear-cart scoping and the checkout
  // redirect for free.
  function setupTryOnce() {
    document.querySelectorAll('.bb-try-once').forEach(function (btn) {
      if (btn.dataset.lvbbWired) return;
      btn.dataset.lvbbWired = 'true';

      btn.addEventListener('click', function () {
        if (btn.dataset.loading === 'true') return;
        var form = btn.closest('#buyboxForm') || document.getElementById('buyboxForm');
        var input = form && form.querySelector('variant-picker input[value="Try Once"]');
        var mainCTA = form && form.querySelector('.bb-main-cta');
        if (!input || !mainCTA) return;

        btn.dataset.loading = 'true';
        var done = false;
        var proceed = function () {
          if (done) return;
          done = true;
          document.removeEventListener('variant:update', proceed);
          mainCTA.click();
          btn.dataset.loading = 'false';
        };

        if (input.checked) {
          proceed();
        } else {
          document.addEventListener('variant:update', proceed);
          setTimeout(proceed, 800); // fallback if the morph never fires the event
          input.click(); // a real click, not checked+dispatchEvent — the native
          // variant-picker only reacts to genuine interaction, same reason
          // enhanceSection() above uses defaultInput.click() to select the
          // default pack. Without it, bbVariant/the select never re-sync,
          // and the main CTA silently submits whatever pack was already
          // selected instead of "Try Once".
        }
      });
    });
  }

  // Root cause of the empty "Select frequency" dropdown: the
  // stay_ai_subscriptions block (snippets/stayai-pdp.liquid) builds that
  // <select>'s options in Liquid, server-side, from
  // `product.selected_or_first_available_variant` — a variant fixed at
  // page-render time. Switching packs via the native picker never
  // reloads the page, so the dropdown stays frozen on whatever plans
  // matched the FIRST variant Shopify picks by default (a legacy one
  // with no valid plan at all), no matter which real pack is selected.
  // This rebuilds it client-side from the `variant:update` event's own
  // resource.selling_plan_allocations, restricted to the plans already
  // configured in the Stay.ai portal (Subscription Setup → Selling
  // Plans) — it doesn't add/guess any plan Stay.ai itself doesn't list.
  // Which plans are "Stay.ai's own" (as opposed to legacy groups still
  // attached to these variants) is read live from
  // <purchase-options sellingPlanGroups="..."> — the same
  // `stay_selling_plan_groups` (app_id-filtered) that
  // snippets/stayai-pdp.liquid already renders for its own use. No plan
  // IDs are hardcoded here: adding, renaming, or reassigning plans in the
  // Stay.ai portal (Subscription Setup → Selling Plans) is picked up
  // automatically on next page load, nothing in this file needs editing.
  function getStayaiSellingPlans() {
    var el = document.querySelector('purchase-options');
    var raw = el && el.getAttribute('sellingPlanGroups');
    if (!raw) return {};
    var groups;
    try {
      groups = JSON.parse(raw);
    } catch (e) {
      return {};
    }
    var map = {};
    (groups || []).forEach(function (group) {
      (group.selling_plans || []).forEach(function (plan) {
        map[String(plan.id)] = plan.name;
      });
    });
    return map;
  }

  // Stay.ai's own widget (heading + frequency dropdown + perks) is hidden by
  // stayai-lvbb-clone-v2.css for visual parity with the live buybox, which left
  // the page with NO visible statement that the purchase auto-renews — the
  // customer only found out at checkout. This reprints the selected plan's own
  // name above the CTA, so the disclosure follows whatever is configured in the
  // Stay.ai portal instead of being hardcoded here.
  function renderPlanTerms(planName) {
    var el = document.getElementById('bbPlanTerms');
    if (!el) return;
    var name = String(planName || '').replace(/\*+\s*$/, '').trim();
    if (!name) {
      el.textContent = '';
      el.hidden = true;
      return;
    }
    el.textContent = 'Auto-renewing subscription · ' + name + '. Pause, skip or cancel anytime.';
    el.hidden = false;
  }

  // The frequency <select> is server-rendered with the plans of whatever variant
  // Shopify picked first, so read it once on load; variant:update keeps it true
  // from then on.
  function syncPlanTermsFromSelect() {
    var select = document.querySelector('select[name="stayai_selling_plan"]');
    var opt = select && select.selectedOptions && select.selectedOptions[0];
    renderPlanTerms(opt && select.value ? opt.textContent : '');
  }

  function setupSubscriptionFrequencySync() {
    document.addEventListener('variant:update', function (e) {
      var select = document.querySelector('select[name="stayai_selling_plan"]');
      if (!select || !e.detail || !e.detail.resource) return;
      var stayaiPlans = getStayaiSellingPlans();
      var allocations = e.detail.resource.selling_plan_allocations || [];
      var match = allocations.filter(function (a) {
        return stayaiPlans.hasOwnProperty(String(a.selling_plan_id));
      })[0];
      select.querySelectorAll('option[data-lvbb-plan]').forEach(function (o) { o.remove(); });
      if (!match) {
        select.value = '';
        renderPlanTerms('');
      } else {
        var opt = document.createElement('option');
        opt.value = String(match.selling_plan_id);
        opt.textContent = stayaiPlans[String(match.selling_plan_id)];
        opt.dataset.lvbbPlan = 'true';
        select.appendChild(opt);
        select.value = String(match.selling_plan_id);
        renderPlanTerms(opt.textContent);
      }
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function init() {
    document.querySelectorAll('.stayai-lvbb-clone-v2-section').forEach(function (section) {
      observeSection(section);
      enhanceSection(section);
    });
    setupClearCartOnAdd();
    setupTryOnce();
    setupSubscriptionFrequencySync();
    syncPlanTermsFromSelect();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
